import { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, useInView } from 'framer-motion';
import { Sparkles, Zap, Target, Brain, CheckCircle2 } from 'lucide-react';

// Simple card reveal animation - NO scale/blur transforms that cause hazy text
function ScrollRevealCard({ children, index, className = '' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.15, duration: 0.5 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function ScrollShowcaseSection() {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  const features = [
    {
      icon: Brain,
      title: 'AI-Powered Analysis',
      description: 'Deep learning models trained on millions of exam papers',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Target,
      title: 'Precision Diagnosis',
      description: 'Identifies exact skill gaps and conceptual weaknesses',
      color: 'from-primary to-teal-400'
    },
    {
      icon: Zap,
      title: 'Instant Results',
      description: 'Get comprehensive reports in under 60 seconds',
      color: 'from-amber-500 to-orange-500'
    }
  ];

  return (
    <section 
      ref={containerRef}
      data-testid="scroll-showcase-section"
      className="relative py-20 md:py-32 overflow-hidden bg-gradient-to-b from-background via-card/30 to-background"
    >
      {/* Static Background Elements - No moving/rotating */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute w-96 h-96 bg-primary/10 rounded-full blur-3xl top-20 -left-48 opacity-40" />
        <div className="absolute w-80 h-80 bg-secondary/10 rounded-full blur-3xl bottom-40 -right-40 opacity-40" />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 relative">
        {/* Section Header - Clean, no blur transforms */}
        <div className="text-center mb-16">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-6"
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Experience the Difference</span>
          </motion.div>

          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4"
          >
            Why Choose
            <span className="text-primary ml-3">Edalyze</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto"
          >
            Transform raw exam data into actionable insights
          </motion.p>
        </div>

        {/* Feature Cards - Smaller, cleaner */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <ScrollRevealCard key={index} index={index}>
              <div className="group relative h-full">
                {/* Card glow effect */}
                <div className={`absolute -inset-1 bg-gradient-to-r ${feature.color} rounded-xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500`} />
                
                <div className="relative bg-card rounded-xl border border-border p-6 h-full group-hover:border-primary/30 transition-all duration-300">
                  {/* Icon - Static, no rotation */}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 shadow-lg`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>

                  <h3 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
                    {feature.title}
                  </h3>
                  
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>

                  {/* Checkmark on hover */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  </div>
                </div>
              </div>
            </ScrollRevealCard>
          ))}
        </div>

        {/* Process Steps - Static, clean with SOLID LINE */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="mt-16 max-w-3xl mx-auto"
        >
          <div className="relative bg-card/50 backdrop-blur-sm rounded-xl border border-border p-6 overflow-hidden">
            {/* Solid connecting line - thicker and better positioned */}
            <div className="absolute top-[2.25rem] left-[15%] right-[15%] h-1 bg-primary/40 rounded-full" />
            
            <div className="relative flex items-center justify-between px-4">
              {['Upload', 'Analyze', 'Diagnose', 'Report'].map((step, index) => (
                <div key={step} className="flex flex-col items-center">
                  <motion.div
                    className="w-10 h-10 rounded-full bg-card border-2 border-primary flex items-center justify-center mb-2 relative z-10"
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{ delay: 0.6 + index * 0.1, type: 'spring' }}
                  >
                    <span className="text-primary font-bold text-sm">{index + 1}</span>
                  </motion.div>
                  <span className="text-xs font-medium">{step}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
